import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useGetDetailedMovimentiQuery, useRealTimeCashoutQuery } from 'features/api/userSlice';
import { useMyBets, useStatoScommessaTabs, useTipoScommessaTabs } from './useMyBetHook';

import { DetailedBetMobile } from 'components/detailedBetMobile';
import { DetailedBetResponseApi } from 'lib/api/detailedBetResponseApi';
import { InternalMenu } from 'components/internalMenu';
import { MyBet as MyBetType } from 'types/dashboard/myBet';
import { ScommesseResponseApiItem } from 'lib/api/scommesseResponseApi';
import { ShareModal } from 'components/modals/shareModal';
import { SkeletonArea } from 'components/skeletonArea';
import { TableMovimentiMyBet } from 'components/tableMovimentiMyBet';
import { TableMovimentiMyBetProps } from 'components/tableMovimentiMyBet/TableMovimentiMyBet';
import { isRealTimeCashoutEnabled } from 'utility/constant';
import { selectIdsRealTimeCashout } from 'features/dashboard/myBets/selectors';
import { setIdsRealTimeCashout } from 'features/dashboard/myBets/myBetSlice';
import styles from './MyBet.module.scss';
import { useGetLabelByKey } from 'hooks/useLingUI';

export type MyBetPropsType = Pick<TableMovimentiMyBetProps, 'labels' | 'isInColumn'> & {
  pageSize?: number;
  canScroll?: boolean;
  cashOutEnabled?: boolean;
  isLocalStateInternalMenu?: boolean;
};

export const MyBet = memo(function MyBet({
  labels,
  pageSize = 6,
  canScroll,
  isInColumn,
  cashOutEnabled = true,
  isLocalStateInternalMenu = true,
}: MyBetPropsType) {
  const dispatch = useAppDispatch();

  const { tipoScommessa } = useTipoScommessaTabs();
  const { tabsStatoScommessa, statoScommessa, setStatoScommessa } = useStatoScommessaTabs();
  const { data, isError, isLoading, totalPages, currentPage, doFetch, setCurrentPage } = useMyBets({ pageSize });

  // const myBetsHref = useGetLabelByKey('mybets-href');
  const apiGenericErrorLabel = useGetLabelByKey('label-api-generic-error');
  const idsRealTimeCashout = useTypedSelector(selectIdsRealTimeCashout);

  const idsRealTimeCashoutRef = useRef<string[]>([]);
  useEffect(() => {
    if (data && isRealTimeCashoutEnabled) {
      const ids = data
        .filter((item: ScommesseResponseApiItem) => item.hasCashout && item.idBiglietto)
        .map((item) => item.idBiglietto!);
      dispatch(setIdsRealTimeCashout(ids));
      idsRealTimeCashoutRef.current = ids;
    }
  }, [data, dispatch]);

  const { data: cashoutResponse } = useRealTimeCashoutQuery(idsRealTimeCashout, {
    pollingInterval: 5000,
    skip: idsRealTimeCashout.length === 0 || !isRealTimeCashoutEnabled,
    refetchOnMountOrArgChange: true,
  });

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedBetMobile, setSelectedBetMobile] = useState<MyBetType>();

  const { data: detailedResponseData, isFetching: isFetchingDetailed } = useGetDetailedMovimentiQuery(
    {
      idBigl: selectedBetMobile?.idBiglietto!,
      idBiglietto: selectedBetMobile?.idBiglietto!,
      codTipoGioco: selectedBetMobile?.codiceTipoGioco!,
    },
    { skip: !selectedBetMobile }
  );

  const detailedResponse = useMemo(() => {
    return detailedResponseData as DetailedBetResponseApi;
  }, [detailedResponseData]);

  const handleBack = () => {
    setSelectedBetMobile(undefined);
  };

  const objContent = useMemo(() => {
    if (isLoading) {
      return (
        <React.Fragment key={`loading-${tipoScommessa}-${statoScommessa}`}>
          <SkeletonArea height="6" />
          <SkeletonArea height="6" />
          <SkeletonArea height="19" />
          <SkeletonArea height="19" />
          <SkeletonArea height="19" />
        </React.Fragment>
      );
    }

    if (isError) {
      return (
        <strong key={`error-${tipoScommessa}-${statoScommessa}`} suppressHydrationWarning>
          {apiGenericErrorLabel}
        </strong>
      );
    }

    return (
      <TableMovimentiMyBet
        key={`list-${tipoScommessa}-${statoScommessa}`}
        list={{ items: data, totalPages }}
        labels={labels}
        verticale={tipoScommessa}
        isInColumn={isInColumn}
        pageNumber={currentPage}
        // myBetsHref={myBetsHref}
        cashOutEnabled={cashOutEnabled}
        cashoutResponse={cashoutResponse}
        selectedStatusTab={statoScommessa}
        // enabledCashOutFilter={cashOutOnly}
        idsRealTimeCashoutRef={idsRealTimeCashoutRef}
        handleSelect={setSelectedBetMobile}
        setPageNumber={setCurrentPage}
        refetchApiBets={doFetch}
        // setEnabledCashOutFilter={setCashOutOnly}
      />
    );
  }, [
    apiGenericErrorLabel,
    cashOutEnabled,
    // cashOutOnly,
    cashoutResponse,
    currentPage,
    data,
    isError,
    isLoading,
    isInColumn,
    labels,
    // myBetsHref,
    totalPages,
    tipoScommessa,
    statoScommessa,
    doFetch,
    // setCashOutOnly,
    setCurrentPage,
  ]);

  return selectedBetMobile ? (
    <React.Fragment>
      <DetailedBetMobile
        type={tipoScommessa}
        items={detailedResponse?.dati}
        detailedResponse={detailedResponse!}
        isFetching={isFetchingDetailed}
        isCarrello={isInColumn}
        selectedBet={selectedBetMobile}
        openModal={() => setShareModalOpen(true)}
        backFunction={handleBack}
        printFunction={() => console.log('print')}
      />
      <ShareModal
        url={`/share-ticket?id=${selectedBetMobile?.idBiglietto}`}
        isOpen={shareModalOpen}
        handleClose={() => setShareModalOpen(false)}
      />
    </React.Fragment>
  ) : (
    <React.Fragment>
      <InternalMenu
        key={`state-${statoScommessa}`}
        items={tabsStatoScommessa}
        smallFont={isInColumn}
        isFullWidth={isInColumn}
        noPaddingTop
        itemSelected={statoScommessa}
        isLocalStateInternalMenu={isLocalStateInternalMenu}
        itemSelectedCallback={setStatoScommessa}
      />
      <div className={canScroll ? styles.scroll : ''}>{objContent}</div>
    </React.Fragment>
  );
});
